import { Pressable } from "react-native";
import { usePathname, useRouter, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { NavSidePanel, type NavSidePanelHeaderRenderProps, type NavSidePanelItem } from "@visioni/a3s-ui";
import { sizes } from "@src/constants/sizes";
import {
  useTenant,
  getTenantDisplayName,
  tenantPath,
  useTenantFeatures,
  holdsFeature,
  useSupportUserStatus,
  resolveSupportFeatureAccess,
} from "@src/features/tenants";
import { useClipCompilationAccess } from "@src/features/clip-compilation/hooks/useClipCompilationAccess";
import { patriotToolsHref } from "@src/features/patriot-tools";
import { Logo } from "./Logo.web";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { useCallback, useMemo, useEffect, type ReactNode } from "react";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import { getActiveRoute, type Route } from "@src/utils/active-route";
import { useSidebarStore } from "@src/stores/sidebar-store";
import { useIsTenantAdmin } from "@visioni/a3s-access/app";
import { useCameraSideNavItems } from "@src/features/cameras/hooks/useCameraSideNavItems";
import { useCameraNavItemRenderer } from "./CameraNavItemContent.web";
import { useReportEntitlements } from "@src/features/reports/hooks/useReportEntitlements";

interface SidepanelProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenTenantSwitcher?: () => void;
}

export function Sidepanel({ collapsed, onToggleCollapse, onOpenTenantSwitcher }: SidepanelProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { siteId: routeSiteId, siteIds: routeSiteIds } = useLocalSearchParams<{
    siteId?: string;
    siteIds?: string;
  }>();
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const tenantName = selectedTenant ? getTenantDisplayName(selectedTenant) : "";
  const storeSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);

  const isOnTrafficFlow = pathname.includes("/traffic-flow");
  const isOnVehicles = pathname.includes("/vehicles");
  const routeSiteForPage = isOnVehicles ? (routeSiteIds ?? null) : (routeSiteId ?? null);
  const selectedSiteId = isOnTrafficFlow || isOnVehicles ? routeSiteForPage : storeSiteId;

  useEffect(() => {
    if (!isOnTrafficFlow && !isOnVehicles) return;
    const next = routeSiteForPage;
    if (next !== useSelectedSiteStore.getState().selectedSiteId) {
      setSelectedSiteId(next);
    }
  }, [isOnTrafficFlow, isOnVehicles, routeSiteForPage, setSelectedSiteId]);

  const isSiteWideControlsEnabled = useBooleanFlag(FEATURE_FLAGS.SITE_WIDE_CONTROLS);
  const isActivityEnabled = useBooleanFlag(FEATURE_FLAGS.ACTIVITY_TAB);

  const { isTenantAdmin } = useIsTenantAdmin();
  const canViewActivity = isActivityEnabled && isTenantAdmin;
  const canViewClipCompilation = useClipCompilationAccess() === "granted";
  // One read of what the tenant is hired for, shared by the entries that gate
  // on it (Reports reads the same cached camera list through its own hook). Each is absent until the answer is known - like the clip
  // compilation entry above - rather than shown and then withdrawn.
  const supportStatus = useSupportUserStatus();
  const tenantFeatures = useTenantFeatures();

  const canViewPatriotTools = supportStatus === "granted";

  const canViewVehicles = holdsFeature(tenantFeatures, "vehicle_tracker");
  // Crossline Mapping is an internal tool over the same line-crossing data
  // RoadSense reports on, so it needs the hire as well as the support role.
  const canViewTrafficFlow = resolveSupportFeatureAccess(supportStatus, tenantFeatures, "line_crossings") === "granted";
  // Reports is offered when a tile would actually appear on it, not merely when
  // the tenant holds some feature - most features grant no report at all. With
  // `roadsense_camera_access` on, WasteSense counts for tenant admins only, so a
  // non-admin whose only tile would be WasteSense gets no entry.
  const reportEntitlements = useReportEntitlements();
  const canViewReports = reportEntitlements.status === "ready" && reportEntitlements.reportTypes.length > 0;

  const activeRoute = useMemo(() => getActiveRoute(pathname), [pathname]);
  const isRouteActive = useCallback((route: Route) => activeRoute === route, [activeRoute]);

  const handleLogoPress = useCallback(() => {
    router.push("/");
  }, [router]);

  const expandedItemIds = useSidebarStore((s) => s.expandedItemIds);
  const setStoreExpandedIds = useSidebarStore((s) => s.setExpandedItemIds);

  const mainItemIds = useMemo(() => ["cameras", "traffic-flow"], []);

  const handleExpandedChange = useCallback(
    (nextIds: readonly string[]) => {
      const newlyExpanded = nextIds.find((id) => !expandedItemIds.includes(id));
      if (!newlyExpanded) {
        setStoreExpandedIds(nextIds);
        return;
      }
      const isTopLevel = mainItemIds.includes(newlyExpanded);
      if (isTopLevel) {
        const childIds = nextIds.filter((id) => !mainItemIds.includes(id));
        setStoreExpandedIds([newlyExpanded, ...childIds]);
      } else {
        setStoreExpandedIds(nextIds);
      }
    },
    [expandedItemIds, setStoreExpandedIds, mainItemIds],
  );

  const getItemBackgroundColour = useCallback(
    (isRouteActive: boolean) => {
      return isRouteActive ? theme.colors.interactive.buttons.hover_background : theme.colors.textSecondary;
    },
    [theme.colors],
  );

  // --- Build NavSidePanel items ---

  const activeCameraKey = useMemo(() => {
    const match = pathname.match(/\/cameras\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
  }, [pathname]);

  const { items: cameraNavItems, itemDataMap } = useCameraSideNavItems({ activeCameraKey });

  const renderItem = useCameraNavItemRenderer({ itemDataMap });

  const mainItems = useMemo((): readonly NavSidePanelItem[] => {
    const items: NavSidePanelItem[] = [
      {
        id: "cameras",
        label: "Cameras",
        icon: <Icon name="videoOutline" size={20} color={getItemBackgroundColour(isRouteActive("cameras"))} />,
        children: cameraNavItems as NavSidePanelItem[],
        selected: isRouteActive("cameras"),
        onPress: () => router.push("/"),
      },
    ];

    if (canViewTrafficFlow) {
      items.push({
        id: "traffic-flow",
        label: "Crossline Mapping",
        icon: <Icon name="motion" size={20} color={getItemBackgroundColour(isRouteActive("traffic-flow"))} />,
        selected: isRouteActive("traffic-flow"),
        // Bare item — no expandable scenario list. Operators know the tenant in
        // advance, so this links straight to the per-tenant worklist (config
        // redesign §0). A site-scoped path is deliberately not used here; site
        // selection happens on the worklist itself.
        onPress:
          selectedTenant?.id ? () => router.push(`/tenants/${selectedTenant.id}/traffic-flow` as never) : undefined,
      });
    }

    if (canViewVehicles) {
      items.push({
        id: "vehicles",
        label: "Vehicle Log",
        icon: <Icon name="truck" size={20} color={getItemBackgroundColour(isRouteActive("vehicles"))} />,
        selected: isRouteActive("vehicles"),
        onPress: () => {
          if (!selectedTenant?.id) return;
          const path =
            selectedSiteId ?
              `/tenants/${selectedTenant.id}/vehicles?siteIds=${selectedSiteId}`
            : `/tenants/${selectedTenant.id}/vehicles`;
          router.push(path as never);
        },
      });
    }

    if (canViewReports) {
      items.push({
        id: "reports",
        label: "Reports",
        icon: <Icon name="reportDocument" size={20} color={getItemBackgroundColour(isRouteActive("reports"))} />,
        selected: isRouteActive("reports"),
        onPress: () => {
          const href = tenantPath(selectedTenant?.id, "reports");
          if (!href) return;
          router.push(href as never);
        },
      });
    }

    return items;
  }, [
    canViewTrafficFlow,
    canViewVehicles,
    canViewReports,
    isRouteActive,
    getItemBackgroundColour,
    router,
    selectedTenant?.id,
    selectedSiteId,
    cameraNavItems,
  ]);

  const footerNavItems = useMemo((): readonly NavSidePanelItem[] => {
    const items: NavSidePanelItem[] = [];

    if (onOpenTenantSwitcher) {
      items.push({
        id: "org-switcher",
        label: tenantName || "Organisation",
        secondaryLabel: tenantName ? "Organisation" : undefined,
        icon: <Icon name="swapVertical" size={20} color={theme.colors.icon} />,
        trailing: <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />,
        onPress: onOpenTenantSwitcher,
        testID: "tenant-switcher-trigger",
      });
    }

    if (isSiteWideControlsEnabled) {
      items.push({
        id: "alarm-settings",
        label: "Controls",
        icon: <Icon name="settingsCamera" size={20} color={getItemBackgroundColour(isRouteActive("alarm-settings"))} />,
        selected: isRouteActive("alarm-settings"),
        onPress: () => {
          if (selectedTenant?.id) {
            router.push(`/tenants/${selectedTenant.id}/alarm-settings` as never);
          }
        },
      });
    }

    if (canViewClipCompilation) {
      items.push({
        id: "clip-compilation",
        label: "Clip Compilations",
        icon: <Icon name="video" size={20} color={getItemBackgroundColour(isRouteActive("clip-compilation"))} />,
        selected: isRouteActive("clip-compilation"),
        onPress: () => {
          if (selectedTenant?.id) {
            router.push(`/tenants/${selectedTenant.id}/clip-compilation` as never);
          }
        },
      });
    }

    if (canViewPatriotTools) {
      items.push({
        id: "monitoring",
        label: "Monitoring",
        icon: <Icon name="shieldSearch" size={20} color={getItemBackgroundColour(isRouteActive("monitoring"))} />,
        selected: isRouteActive("monitoring"),
        onPress: () => {
          if (selectedTenant?.id) {
            router.push(patriotToolsHref(selectedTenant.id));
          }
        },
      });
    }

    if (canViewActivity) {
      items.push({
        id: "audit-log",
        label: "Audit Log",
        icon: <Icon name="eventOutline" size={20} color={getItemBackgroundColour(isRouteActive("audit-log"))} />,
        selected: isRouteActive("audit-log"),
        onPress: () => {
          if (selectedTenant?.id) {
            router.push(`/tenants/${selectedTenant.id}/activity` as never);
          }
        },
      });
    }

    items.push({
      id: "logout",
      label: "Log out",
      icon: <Icon name="logout" size={20} color={theme.colors.danger[400]} />,
      onPress: () => router.push("/auth/logout"),
    });

    return items;
  }, [
    onOpenTenantSwitcher,
    tenantName,
    getItemBackgroundColour,
    isSiteWideControlsEnabled,
    canViewActivity,
    canViewClipCompilation,
    canViewPatriotTools,
    isRouteActive,
    selectedTenant?.id,
    router,
    theme.colors,
  ]);

  const renderHeader = useCallback(
    ({ minimized, minimizeButton }: NavSidePanelHeaderRenderProps): ReactNode => {
      if (minimized) {
        return (
          <View alignItems="center" gap="xl">
            <Pressable onPress={handleLogoPress} accessibilityRole="button">
              <Logo collapsed />
            </Pressable>
            {minimizeButton}
          </View>
        );
      }

      return (
        <View gap="2xl">
          <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" height={36}>
            <Pressable onPress={handleLogoPress} accessibilityRole="button">
              <Logo />
            </Pressable>
            {minimizeButton}
          </View>
        </View>
      );
    },
    [handleLogoPress],
  );

  return (
    <NavSidePanel
      header={<Logo collapsed={collapsed} />}
      items={mainItems}
      footerItems={footerNavItems}
      minimizable
      minimized={collapsed}
      onMinimizedChange={onToggleCollapse}
      width={sizes.webSidebarWidth}
      minimizedWidth={68}
      expandedItemIds={expandedItemIds}
      onExpandedItemIdsChange={handleExpandedChange}
      renderHeader={renderHeader}
      renderItem={renderItem}
      style={styles.panel}
    />
  );
}

const styles = StyleSheet.create(() => ({
  panel: {
    borderRadius: sizes.radius,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
    borderLeftWidth: 0,
    transition: "width 200ms ease-in-out",
  },
}));
